import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Box, Button, Typography, Alert, TextField } from '@mui/material';
import { LockReset, ArrowBack } from '@mui/icons-material';
import { useResetPassword } from '../../application/useResetPassword';
import AuthCardLayout from './AuthCardLayout';
import PasswordField from './PasswordField';

/** UC-3: usa useResetPassword (T26). Pantalla alternativa sin token, antes del formulario. */
export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const { error, success, loading, submit } = useResetPassword(token);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // UC-3: el toggle de "Nueva contraseña" también afecta la visibilidad de "Confirmar
  // contraseña" (comparten el mismo `showPassword`, sin botón propio en el segundo campo).
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit(newPassword, confirmPassword);
  };

  if (!token) {
    return (
      <AuthCardLayout>
        <Box sx={{ textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            Enlace inválido o incompleto. Solicita un nuevo enlace de restablecimiento.
          </Alert>
          <Button component={Link} to="/forgot-password" variant="contained" sx={{ mt: 2 }}>
            Solicitar nuevo enlace
          </Button>
        </Box>
      </AuthCardLayout>
    );
  }

  return (
    <AuthCardLayout title="Nueva Contraseña">
      {success ? (
        <>
          <Alert severity="success" sx={{ mb: 2 }}>
            ¡Contraseña restablecida exitosamente! Ya puedes iniciar sesión con tu nueva contraseña.
          </Alert>
          <Button
            fullWidth
            variant="contained"
            onClick={() => navigate('/login')}
            sx={{ mt: 2, py: 1.5 }}
          >
            Ir al Login
          </Button>
        </>
      ) : (
        <>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ingresa tu nueva contraseña (mínimo 8 caracteres).
          </Typography>

          <form onSubmit={handleSubmit}>
            <PasswordField
              fullWidth
              label="Nueva contraseña"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              margin="normal"
              autoComplete="new-password"
              disabled={loading}
              show={showPassword}
              onToggleShow={() => setShowPassword((s) => !s)}
            />
            {/* Confirmar contraseña: sin botón de toggle propio, comparte showPassword (UC-3). */}
            <TextField
              fullWidth
              label="Confirmar contraseña"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              autoComplete="new-password"
              disabled={loading}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={<LockReset />}
              sx={{ mt: 3, py: 1.5 }}
            >
              {loading ? 'Restableciendo...' : 'Restablecer Contraseña'}
            </Button>
          </form>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Button component={Link} to="/login" startIcon={<ArrowBack />} size="small">
              Volver al Login
            </Button>
          </Box>
        </>
      )}
    </AuthCardLayout>
  );
}
